import { useEffect } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import client from '../API';

/**
 * A hook that registers for APNs on iOS, listens for the token,
 * stores it in Preferences, and sends it to the backend.
 */
const usePushNotifications = () => {
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') {
      console.info('[Push] iOS only');
      return;
    }

    let registrationListener: any;
    let errorListener: any;

    const setup = async () => {
      // 1️⃣ Request permission
      const { receive } = await PushNotifications.requestPermissions();
      if (receive !== 'granted') {
        console.warn('[Push] Permission denied');
        return;
      }

      // 2️⃣ Listen *before* register
      registrationListener = await PushNotifications.addListener(
        'registration',
        async ({ value }) => {
          if (!value) {
            console.error('[Push] Empty token');
            return;
          }
          console.info('[Push] APNs token:', value);
          // store to Preferences
          await Preferences.set({ key: 'apns_token', value });
          // send to backend
          try {
            await client.notifications.registerDeviceToken({
              device_token: value,
              platform: 'ios',
            });
            console.info('[Push] Token sent to backend');
          } catch (err) {
            console.error('[Push] registerDeviceToken failed', err);
          }
        }
      );

      errorListener = await PushNotifications.addListener(
        'registrationError',
        (err) => console.error('[Push] Registration error:', err)
      );

      // 3️⃣ Kick off registration
      await PushNotifications.register();
      console.info('[Push] Registration initiated');

      // 4️⃣ If we already have a stored token, resend it
      const { value: stored } = await Preferences.get({ key: 'apns_token' });
      if (stored) {
        console.info('[Push] Resending stored token:', stored);
        try {
          await client.notifications.registerDeviceToken({
            device_token: stored,
            platform: 'ios',
          });
        } catch (err) {
          console.error('[Push] resend failed', err);
        }
      }
    };

    setup().catch((e) => console.error('[Push] setup error', e));

    return () => {
      registrationListener?.remove();
      errorListener?.remove();
    };
  }, []);
};

export default usePushNotifications;
